import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Auth
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/portal/ProtectedRoute';
import PortalSidebar from './components/portal/PortalSidebar';

// Portal pages
import PortalLogin from './pages/portal/Login';
import AdminDashboard from './pages/portal/admin/AdminDashboard';
import UserManagement from './pages/portal/admin/UserManagement';
import AdminDailyUpdates from './pages/portal/admin/AdminDailyUpdates';
import AdminTasks from './pages/portal/admin/AdminTasks';
import AdminTodos from './pages/portal/admin/AdminTodos';
import AdminLeaves from './pages/portal/admin/AdminLeaves';
import AdminAttendance from './pages/portal/admin/AdminAttendance';
import EmployeeDashboard from './pages/portal/employee/EmployeeDashboard';
import EmployeeAttendance from './pages/portal/employee/EmployeeAttendance';
import EmployeeProfile from './pages/portal/employee/EmployeeProfile';
import EmployeePage from './components/portal/EmployeePage';
import DailyUpdatesTab from './pages/portal/employee/tabs/DailyUpdatesTab';
import TasksTab from './pages/portal/employee/tabs/TasksTab';
import LeaveRequestTab from './pages/portal/employee/tabs/LeaveRequestTab';
import PersonalTodosPanel from './components/portal/PersonalTodosPanel';

// Main website components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import ChatBot from './components/common/ChatBot';
import WhatsAppButton from './components/common/WhatsAppButton';

// Hooks
import useSmoothScroll from './hooks/useSmoothScroll';
import useScrollReveal from './hooks/useScrollReveal';

// Main website pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Projects from './pages/Projects';
import Contact from './pages/Contact';
import Career from './pages/Career';
import NotFound from './pages/NotFound';
import TermsAndCondition from './pages/TermsAndCondition';
import PrivacyPolicy from './pages/PrivacyPolicy';

// ── Portal layouts ─────────────────────────────────────────────────────────────
function AdminLayout() {
  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC]">
      <PortalSidebar role="admin" />
      <main className="flex-1 overflow-y-auto">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="updates" element={<AdminDailyUpdates />} />
          <Route path="tasks" element={<AdminTasks />} />
          <Route path="todos" element={<AdminTodos />} />
          <Route path="leaves" element={<AdminLeaves />} />
          <Route path="attendance" element={<AdminAttendance />} />
          <Route path="*" element={<Navigate to="/portal/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function EmployeeLayout() {
  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC]">
      <PortalSidebar role="employee" />
      <main className="flex-1 overflow-y-auto">
        <Routes>
          <Route index element={<EmployeeDashboard />} />
          <Route
            path="updates"
            element={
              <EmployeePage title="Daily Updates" subtitle="Log what you completed, what's in progress, and any notes.">
                <DailyUpdatesTab />
              </EmployeePage>
            }
          />
          <Route
            path="tasks"
            element={
              <EmployeePage title="My Tasks" subtitle="Drag a card between columns to update its status.">
                <TasksTab />
              </EmployeePage>
            }
          />
          <Route
            path="todos"
            element={
              <EmployeePage title="Personal TODO" subtitle="Your private task list — only you can see this.">
                <PersonalTodosPanel />
              </EmployeePage>
            }
          />
          <Route
            path="leave"
            element={
              <EmployeePage title="Leave Requests" subtitle="Apply for time off and view your request history.">
                <LeaveRequestTab />
              </EmployeePage>
            }
          />
          <Route path="profile" element={<EmployeeProfile />} />
          <Route path="attendance" element={<EmployeeAttendance />} />
          <Route path="*" element={<Navigate to="/portal/employee" replace />} />
        </Routes>
      </main>
    </div>
  );
}

// ── Main website ───────────────────────────────────────────────────────────────
function ScrollRevealRunner() {
  useScrollReveal();
  return null;
}

function MainWebsite() {
  const [darkMode] = useState(true);
  useSmoothScroll();

  useEffect(() => {
    localStorage.setItem('darkMode', 'true');
    AOS.init({ duration: 1000, once: true, mirror: false });
  }, []);

  return (
    <div className="light">
      <div className="flex flex-col min-h-screen bg-paper text-ink transition-colors duration-300">
        <Navbar darkMode={darkMode} toggleDarkMode={() => {}} />
        <ScrollToTop />
        <ScrollRevealRunner />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/web-development" element={<Services />} />
            <Route path="/services/mobile-app-development" element={<Services />} />
            <Route path="/services/ui-ux-design" element={<Services />} />
            <Route path="/services/digital-marketing" element={<Services />} />
            <Route path="/services/cloud-services" element={<Services />} />
            <Route path="/services/ai-integration" element={<Services />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/career" element={<Career />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms-conditions" element={<TermsAndCondition />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <WhatsAppButton />
        <ChatBot />
        <Footer />
      </div>
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────────────────────
function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Portal routes — no main Navbar/Footer */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route
            path="/portal/admin/*"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/employee/*"
            element={
              <ProtectedRoute requiredRole="employee">
                <EmployeeLayout />
              </ProtectedRoute>
            }
          />
          <Route path="/portal" element={<Navigate to="/portal/login" replace />} />

          {/* Main website */}
          <Route path="/*" element={<MainWebsite />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
